/**
 * Permission utility functions for access control
 */

export interface Permission {
  feature_id: number;
  feature_name: string;
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
}

/** API / storage may send flags as strings; only explicit truth grants access. */
export function coercePermissionBool(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return value !== 0 && !Number.isNaN(value);
  }
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no" || s === "") return false;
    const n = Number(s);
    if (s !== "" && !Number.isNaN(n)) return n !== 0;
  }
  return false;
}

function normalizePermission(raw: Record<string, unknown>): Permission {
  const fid = raw.feature_id;
  const feature_id =
    typeof fid === "number" && !Number.isNaN(fid)
      ? fid
      : Number(fid);
  return {
    feature_id: Number.isNaN(feature_id) ? 0 : feature_id,
    feature_name: String(raw.feature_name ?? ""),
    view: coercePermissionBool(raw.view),
    add: coercePermissionBool(raw.add),
    update: coercePermissionBool(raw.update),
    delete: coercePermissionBool(raw.delete),
  };
}

export function normalizePermissionList(list: unknown[]): Permission[] {
  return list
    .filter((p) => p && typeof p === "object" && !Array.isArray(p))
    .map((p) => normalizePermission(p as Record<string, unknown>));
}

/**
 * Get permissions from localStorage
 * Falls back to checking user object in localStorage if user_permissions is not found
 */
export function getPermissions(): Permission[] {
  try {
    // First, try to get from user_permissions
    const permissionsStr = localStorage.getItem("user_permissions");
    if (permissionsStr) {
      const parsed = JSON.parse(permissionsStr) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return normalizePermissionList(parsed);
      }
    }

    // Fallback: try to get from user object
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr) as any;
        if (user?.permissions && Array.isArray(user.permissions)) {
          const normalized = normalizePermissionList(user.permissions);
          // Store normalized copy for future use
          localStorage.setItem("user_permissions", JSON.stringify(normalized));
          return normalized;
        }
      } catch (userError) {
        console.error("❌ [Permissions] Failed to parse user object:", userError);
      }
    }

    return [];
  } catch (error) {
    console.error("❌ [Permissions] Failed to get permissions:", error);
    return [];
  }
}

/**
 * Check if user has permission for a specific feature and action
 * @param featureId - The feature ID to check
 * @param action - The action to check (view, add, update, delete)
 * @returns true if user has permission, false otherwise
 */
export function hasPermission(
  featureId: number,
  action: "view" | "add" | "update" | "delete"
): boolean {
  const permissions = getPermissions();
  const fid = Number(featureId);
  const permission = permissions.find(
    (p) => Number(p.feature_id) === fid,
  );
  if (!permission) {
    console.log(`🔍 [Permissions] No permission found for feature_id: ${featureId}`);
    return false;
  }
  return coercePermissionBool(permission[action]);
}

/**
 * Check if user has view permission for a feature
 */
export function canView(featureId: number): boolean {
  return hasPermission(featureId, "view");
}

/**
 * Check if user has add permission for a feature
 */
export function canAdd(featureId: number): boolean {
  return hasPermission(featureId, "add");
}

/**
 * Check if user has update permission for a feature
 */
export function canUpdate(featureId: number): boolean {
  return hasPermission(featureId, "update");
}

/**
 * Check if user has delete permission for a feature
 */
export function canDelete(featureId: number): boolean {
  return hasPermission(featureId, "delete");
}

/**
 * Check if user has add or update permission (for Add/Edit operations)
 */
export function canAddOrUpdate(featureId: number): boolean {
  return canAdd(featureId) || canUpdate(featureId);
}

/**
 * Get permission object for a specific feature
 */
export function getFeaturePermission(featureId: number): Permission | null {
  const permissions = getPermissions();
  const fid = Number(featureId);
  return permissions.find((p) => Number(p.feature_id) === fid) || null;
}

/**
 * Feature name to ID mapping (for dynamic lookup)
 * This allows checking permissions by feature name instead of hardcoded IDs
 */
export const FEATURE_NAMES = {
  FACILITY_ANALYSIS: "Facility Analysis",
  POTENTIAL_PENALTIES: "Potential Penalties",
  EXCLUSION_SANCTION_CHECK: "Exclusion / Sanction Check",
  CONTINUOUS_COMPLIANCE: "Continuous Compliance",
  RISK_ANALYSIS: "Risk Analysis",
  PAYOR_CONTRACT_ANALYSIS: "Payor Contract Analysis",
  MOCK_SURVEY: "Mock Survey",
  USER_MANAGEMENT: "User Management",
  BILLINGS: "Billings",
  AUDIT_LOGS: "Audit Logs",
  /** Must match backend `feature_name` for Learning Center (see features API). */
  LEARNING_CENTER: "Learning Center",
  AGREEMENTS: "Agreements",
  DISCOVER: "Discover",
  // NOTE: This key name is used for permission lookup against the backend,
  // which still stores the feature as "Setup Facility". Keep this value as-is
  // even though the UI labels show "Regulations Library".
  SETUP_FACILITY: "Setup Facility",
  ADD_FACILITY: "Add Facility",
  DASHBOARD: "Dashboard",
} as const;

/** Temporarily omit from User Management / Add User permission matrices. */
export function filterFeaturesForPermissionEditor<
  T extends { feature_name?: string; name?: string },
>(features: T[]): T[] {
  const hiddenName = FEATURE_NAMES.PAYOR_CONTRACT_ANALYSIS.toLowerCase();
  return features.filter((feature) => {
    const name = (feature.feature_name || feature.name || "")
      .trim()
      .toLowerCase();
    return name !== hiddenName;
  });
}

/**
 * UI label for a feature. Backend `feature_name` stays unchanged
 * ({@link FEATURE_NAMES.SETUP_FACILITY}, {@link FEATURE_NAMES.ADD_FACILITY}).
 */
export function getFeatureDisplayName(
  featureName: string | null | undefined,
): string {
  const raw = (featureName ?? "").trim();
  if (!raw) return "";
  if (raw.toLowerCase() === FEATURE_NAMES.SETUP_FACILITY.toLowerCase()) {
    return "Regulations Library";
  }
  // Bug 10002: invite matrix shows "Facility", not "Add Facility".
  if (raw.toLowerCase() === FEATURE_NAMES.ADD_FACILITY.toLowerCase()) {
    return "Facility";
  }
  return raw;
}

/**
 * Resolved permission row for a feature name (after normalization).
 * Prefer this over getFeatureIdByName + hasPermission to avoid id type mismatches.
 */
export function getPermissionByFeatureName(
  featureName: string,
): Permission | null {
  const permissions = getPermissions();
  const key = featureName.trim().toLowerCase();
  const permission = permissions.find(
    (p) => p.feature_name?.trim().toLowerCase() === key,
  );
  return permission ?? null;
}

/**
 * Get feature ID by feature name (more resilient to database ID changes)
 * @param featureName - The feature name to look up
 * @returns The feature ID if found, null otherwise
 */
export function getFeatureIdByName(featureName: string): number | null {
  const p = getPermissionByFeatureName(featureName);
  return p?.feature_id ?? null;
}

/**
 * Check permission by feature name instead of ID (more resilient)
 * @param featureName - The feature name to check
 * @param action - The action to check (view, add, update, delete)
 * @returns true if user has permission, false otherwise
 */
export function hasPermissionByName(
  featureName: string,
  action: "view" | "add" | "update" | "delete"
): boolean {
  const permission = getPermissionByFeatureName(featureName);
  if (!permission) {
    return false;
  }
  return coercePermissionBool(permission[action]);
}

/**
 * Convenience functions for checking permissions by feature name
 */
export function canViewByName(featureName: string): boolean {
  return hasPermissionByName(featureName, "view");
}

export function canAddByName(featureName: string): boolean {
  return hasPermissionByName(featureName, "add");
}

export function canUpdateByName(featureName: string): boolean {
  return hasPermissionByName(featureName, "update");
}

export function canDeleteByName(featureName: string): boolean {
  return hasPermissionByName(featureName, "delete");
}

/** Facility- or feature-scoped update check (falls back to global permissions). */
export function canUpdateFeatureFromPermissions(
  facilityPermissions: Permission[],
  featureName: string,
): boolean {
  if (!facilityPermissions.length) {
    return canUpdateByName(featureName);
  }
  const key = featureName.trim().toLowerCase();
  const permission = facilityPermissions.find(
    (p) => p.feature_name?.trim().toLowerCase() === key,
  );
  return permission ? coercePermissionBool(permission.update) : false;
}

/** Mock Survey comment/edit/download actions require update permission. */
export function canUpdateMockSurveyFromPermissions(
  facilityPermissions: Permission[],
): boolean {
  return canUpdateFeatureFromPermissions(
    facilityPermissions,
    FEATURE_NAMES.MOCK_SURVEY,
  );
}


